import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import PostCard from "@/components/PostCard";
import AmbientBackground from "@/components/AmbientBackground";

/**
 * Standalone post page — used for shared links.
 * Fully public: anonymous visitors can read the post.
 * Interactive actions (like / comment / share) prompt sign-in via PostCard.
 */
export default function PostDetail() {
  const { postId } = useParams();
  const { user, login } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${postId}`);
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "notfound" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId]);

  return (
    <div className="min-h-screen relative bg-[#0A0A0A] text-white" data-testid="post-detail-page">
      <AmbientBackground />

      {/* Slim top bar (no protected layout — this page is public) */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-white/5">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-4 py-3">
          <Link
            to={user ? "/feed" : "/"}
            className="flex items-center gap-2 text-white/70 hover:text-white transition"
            data-testid="post-detail-back"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back</span>
          </Link>

          <Link to={user ? "/feed" : "/"} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#00E676] flex items-center justify-center">
              <TrendingUp size={16} strokeWidth={2.5} className="text-black" />
            </div>
            <span className="font-bold tracking-tight">Cashflow</span>
          </Link>

          {user ? (
            <Link
              to="/feed"
              className="text-sm text-white/70 hover:text-white transition"
              data-testid="post-detail-feed-link"
            >
              Feed
            </Link>
          ) : (
            <button
              onClick={() => login(`/p/${postId}`)}
              className="cta-green rounded-md px-3.5 py-1.5 text-sm font-semibold"
              data-testid="post-detail-signin-btn"
            >
              Sign in
            </button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 relative z-10">
        {loading && (
          <div className="surface rounded-xl p-6 animate-pulse text-white/40 text-sm">
            Loading post…
          </div>
        )}

        {!loading && error === "notfound" && (
          <div className="surface rounded-xl p-8 text-center" data-testid="post-detail-notfound">
            <div className="text-2xl font-bold mb-2">Post not found</div>
            <p className="text-white/50 mb-6 text-sm">
              This post may have been deleted or the link is incorrect.
            </p>
            <Link
              to={user ? "/feed" : "/"}
              className="cta-green inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold"
            >
              Open Cashflow →
            </Link>
          </div>
        )}

        {!loading && error === "error" && (
          <div className="surface rounded-xl p-6 text-center text-white/60 text-sm" data-testid="post-detail-error">
            Something went wrong loading this post. Try refreshing.
          </div>
        )}

        {!loading && !error && post && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {!user && (
              <div
                className="mb-5 surface rounded-xl p-4 flex items-center justify-between gap-4 border border-[#00E676]/25"
                data-testid="guest-cta-banner"
              >
                <div>
                  <div className="text-sm font-semibold">Join Cashflow to like &amp; comment</div>
                  <div className="text-xs text-white/50 mt-0.5">
                    Verified income proofs · AI Money Coach · Live leaderboard
                  </div>
                </div>
                <button
                  onClick={() => login(`/p/${postId}`)}
                  className="cta-green rounded-md px-4 py-2 text-sm font-semibold shrink-0"
                  data-testid="guest-cta-signin"
                >
                  Sign in
                </button>
              </div>
            )}

            <PostCard post={post} />

            <div className="mt-8 text-center">
              <Link
                to={user ? "/feed" : "/"}
                className="text-sm text-white/45 hover:text-[#00E676] transition font-mono"
                data-testid="post-detail-more-link"
              >
                {user ? "← Back to feed" : "See more posts on Cashflow →"}
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
